import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AG_GRID_LOCALE_BR } from '@models/locale';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { getBackendErrorMessage, isPermissionDeniedError } from '@utils/backend-error.utils';

@Injectable({
  providedIn: 'root',
})
export class SystemService {
  private static instance: SystemService;

  public static getInstance(): SystemService {
    if (!SystemService.instance) {
      throw new Error('SystemService not initialized');
    }
    return SystemService.instance;
  }

  AUTH_PROVIDER: "cognito" | "svm" = "svm";

  localeText = AG_GRID_LOCALE_BR;

  constructor(
    private toastr: ToastrService,
    public router: Router
  ) {
    SystemService.instance = this;
  }

  back() {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    segments.pop();
    const previousUrl = segments.join('/');
    return this.router.navigate([previousUrl]);
    // return this.router.getCurrentNavigation()?.previousNavigation;
  }

  navigateTo(route: any[]) {
    this.router.navigate(route);
  }

  setButtonIcon(selector: string, iconName: string) {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.innerHTML = `<app-icon name="${iconName}"></app-icon>`;
      }
    }, 0);
  }

  formatDateToInput(timestamp: { seconds: number; nanos: number } | undefined): string {
    if (!timestamp || !timestamp.seconds) {
      return '';
    }
    const date = new Date(timestamp.seconds * 1000);
    const timezoneOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
    const localISOTime = new Date(date.getTime() - timezoneOffset).toISOString().slice(0, -1);
    return localISOTime.substring(0, 16); // YYYY-MM-DDTHH:mm
  }

  showToast(
    message: string,
    title: string,
    type: 'success' | 'error' | 'info' | 'warning',
    options?: Partial<IndividualConfig>
  ) {
    this.toastr[type](message, title, {
      positionClass: 'toast-bottom-center',
      closeButton: true,
      timeOut: 2000,
      ...options,
    });
  }

  showAlert(options: SweetAlertOptions) {
    return Swal.fire({
      confirmButtonText: 'OK',
      icon: 'warning',
      customClass: {
        confirmButton:
          'bg-primary hover:bg-primary-hover text-white font-semibold px-6 py-2 rounded-md focus:outline-none m-4',
        cancelButton:
          'bg-gray-600 hover:bg-secondary text-white font-semibold px-6 py-2 rounded-md focus:outline-none m-4',
      },
      buttonsStyling: false,
      ...options, // Permite personalização total
    });
  }

  showSuccessAlert(message: string, title: string = 'Sucesso!') {
    return this.showAlert({
      title,
      text: message,
      icon: 'success',
    });
  }

  showWarningAlert(message: string, title: string = 'Atenção!') {
    return this.showAlert({
      title,
      text: message,
      icon: 'warning',
    });
  }

  showErrorAlert(message: string, title: string = 'Atenção!') {
    return this.showAlert({
      title,
      text: message,
      icon: 'error',
    });
  }

  showBackendErrorAlert(error: unknown, fallback: string, title: string = 'Erro') {
    const resolvedTitle = isPermissionDeniedError(error) ? 'Sem permissão' : title;

    return this.showAlert({
      title: resolvedTitle,
      text: getBackendErrorMessage(error, fallback),
      icon: 'error',
    });
  }

  showInfoAlert(message: string, title: string = 'Atenção') {
    return this.showAlert({
      title,
      text: message,
      icon: 'info',
    });
  }

  showSuccessToast(message: string, title: string = 'Sucesso!') {
    this.showToast(message, title, 'success');
  }

  showWarningToast(message: string, title: string = 'Atenção!') {
    this.showToast(message, title, 'warning');
  }

  showErrorToast(message: string, title: string = 'Atenção!') {
    this.showToast(message, title, 'error');
  }

  showInfoToast(message: string, title: string = 'Atenção!') {
    this.showToast(message, title, 'info');
  }
}
